import React from "react";
import { Text } from "../text/text";
import styles from "./price.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Price — the dark-mode price display. Two mutually exclusive modes: `dollars`
 * plus `cents` segments in mixed typography (split, the default, matching the
 * FUSION ECLIPSE Price Point set), or `value` as ONE text run (normal, body-3
 * at a uniform 14px, used by PriceBlock's strikethrough line).
 *
 * Server Component, and an inline `<span>` so it drops into a card, a table
 * cell or beside a button without a block break. No size prop — font-size
 * adapts through the token layer's own @media ladder. price.module.css carries
 * the values and the disagreements with the frame.
 *
 * For a price wrapped with a strikethrough previous price, a secondary line or
 * legal copy underneath, use the PriceBlock molecule.
 */
/**
 * Where the frequency suffix sits — `inline` on the price's shared baseline
 * (default), or `below` on its own row when a narrow card would crowd it.
 */
export type PriceFrequencyPlacement = "inline" | "below";

interface PriceCommonProps {
  className?: string;
}

/** Split mode — dollars + cents + an optional trailing suffix. */
export interface PriceSplitProps extends PriceCommonProps {
  value?: never;
  /**
   * The dollar portion, incl. currency sign — e.g. "$9". Required: a floating
   * cents run with no amount is not a state the design publishes.
   */
  dollars: string;
  /** The cents-and-frequency portion — e.g. ".99/mo". */
  cents?: string;
  /**
   * Suffix after the price, e.g. "+ taxes & fees", rendered through the Text
   * atom. Omit for a bare price with no trailing label.
   */
  frequency?: string;
  /** Where the suffix sits — inline with the price (default), or on its own row. */
  frequencyPlacement?: PriceFrequencyPlacement;
}

/** Normal mode — the whole price as ONE text run in one style. */
export interface PriceNormalProps extends PriceCommonProps {
  /**
   * The whole price as ONE string in one type style, e.g. "$74.99/mo.".
   * Setting it selects normal mode and the split-segment props go away.
   */
  value: string;
  dollars?: never;
  cents?: never;
  frequency?: never;
  frequencyPlacement?: never;
}

export type PriceProps = PriceSplitProps | PriceNormalProps;

/**
 * Presence test for caller-supplied content, shared with PriceBlock.
 *
 * `Boolean(" ")` and `Boolean(<></>)` are both `true`, and these fields are
 * CMS-authorable, so both shapes really arrive. `React.Children.toArray` is not
 * enough either — it treats a fragment as ONE child — so fragments are recursed
 * into and contribute only what is inside them.
 */
export function isPresent(value: React.ReactNode): boolean {
  if (value === null || value === undefined || typeof value === "boolean") {
    return false;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.some(isPresent);
  }
  if (React.isValidElement(value)) {
    if (value.type === React.Fragment) {
      return isPresent((value.props as { children?: React.ReactNode }).children);
    }
    return true;
  }
  return Boolean(value);
}

function PriceImpl({
  value,
  dollars,
  cents,
  frequency,
  frequencyPlacement = "inline",
  className
}: PriceProps) {
  // `typeof`, not truthiness: `value=""` is legal under PriceNormalProps and
  // must render an empty text run rather than fall through to split mode.
  if (typeof value === "string") {
    const normalClasses = [styles.normal, className].filter(Boolean).join(" ");
    return (
      <Text as="span" className={normalClasses} variant="body-3">
        {value}
      </Text>
    );
  }

  const classes = [
    styles.root,
    frequencyPlacement === "below" ? styles.below : null,
    className
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <span className={classes}>
      <span className={styles.price}>
        <span className={styles.dollars}>{dollars}</span>
        {/* `cents` is optional — a whole-dollar price is a supported shape, and
            Code Connect emits one whenever the Figma `Cents` text is empty. */}
        {isPresent(cents) && (
          <span className={styles.centsBox}>
            <span className={styles.cents}>{cents}</span>
          </span>
        )}
      </span>
      {isPresent(frequency) && (
        <span className={styles.freqBox}>
          <Text as="span" className={styles.frequency} variant="label">
            {frequency}
          </Text>
        </span>
      )}
    </span>
  );
}
export const Price = withSafeRender(PriceImpl, "Price");

export default Price;
